import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import TableCard from 'lib/designSystem/Card/TableCard';
import { formatMoney, getShortString } from 'lib/functions/utils';
import {
  AmountSummaryHeader,
  AmountSummarySection,
} from 'pages/Finance/Components/Financing/common/Summary';
import React from 'react';

interface ConfirmingPayment {
  amount: number;
  businessDestinationIdentifier?: string;
  businessDestinationName?: string;
  executedAt?: Date | string;
}

interface ConfirmingRequest {
  payments?: ConfirmingPayment[];
}

interface AmountSummaryProps {
  request: ConfirmingRequest;
  onExport?: () => void | Promise<void>;
}

const AmountSummary: React.FC<AmountSummaryProps> = ({ request, onExport }) => {
  const paymentsAmount = (request.payments || []).reduce((acc, payment) => {
    return acc + payment.amount;
  }, 0);

  const hasCompletedPayments = (request.payments || []).some(
    (payment) => payment.executedAt,
  );

  const groupedPayments = Object.values(
    (request.payments || []).reduce(
      (acc, curr) => {
        const id = curr.businessDestinationIdentifier || '';
        if (!acc[id]) {
          acc[id] = {
            businessIdentifier: id,
            businessName: curr.businessDestinationName || '',
            totalAmount: 0,
          };
        }
        acc[id].totalAmount += curr.amount;
        return acc;
      },
      {} as Record<
        string,
        {
          businessIdentifier: string;
          businessName: string;
          totalAmount: number;
        }
      >,
    ),
  );

  const exportButton =
    hasCompletedPayments && onExport ? (
      <IconButton
        size="small"
        onClick={onExport}
        sx={{
          color: 'primary.main',
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
          '&:hover': {
            backgroundColor: 'grey.100',
            borderColor: 'grey.300',
          },
          borderRadius: '100px',
          padding: '4px 12px',
        }}
      >
        <FileDownloadIcon sx={{ fontSize: '1rem', paddingRight: '4px' }} />
        <Typography variant="caption" fontWeight={600}>
          Exportar
        </Typography>
      </IconButton>
    ) : undefined;

  const items = [
    ...groupedPayments.map((payment) => ({
      label: getShortString(payment.businessName, 30),
      value: payment.totalAmount,
    })),
    {
      label: 'Total a pagar',
      value: paymentsAmount,
      valueColor: 'primary.main' as const,
      showDivider: true,
    },
  ];

  return (
    <TableCard
      size="medium"
      enabledEffects={false}
      sx={{ boxShadow: 'none', background: 'transparent' }}
    >
      <AmountSummaryHeader
        title="Resumen Montos"
        totalAmount={paymentsAmount}
        formatMoney={formatMoney}
        actionButton={exportButton}
      />
      <AmountSummarySection items={items} formatMoney={formatMoney} />
    </TableCard>
  );
};

export default AmountSummary;
